import React, { useState, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';

interface AccordionItemProps {
  title: string | React.ReactNode;
  description?: string;
  defaultOpen?: boolean;
  /** Controlled: when provided, open state is controlled by parent */
  open?: boolean;
  /** Controlled: called when user toggles; use with open for controlled mode */
  onToggle?: (open: boolean) => void;
  /** Optional key for parent to track which section is open (e.g. for Accordion openKeys) */
  itemKey?: string;
  /** Renders in the header row outside the main toggle (e.g. icon buttons; avoids nested buttons) */
  headerAction?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  /** Applied to the title toggle button */
  headerClassName?: string;
  /** Applied to the full header bar (title + headerAction) */
  headerBarClassName?: string;
  /** Applied to the headerAction / chevron side panel */
  headerActionClassName?: string;
}

export const AccordionItem: React.FC<AccordionItemProps> = ({
  title,
  description,
  defaultOpen = false,
  open: controlledOpen,
  onToggle,
  itemKey: _itemKey,
  headerAction,
  children,
  className,
  headerClassName,
  headerBarClassName,
  headerActionClassName,
}) => {
  const { colors } = useTheme();
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined && onToggle !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const setOpen = useCallback(
    (next: boolean) => {
      if (isControlled) onToggle(next);
      else setUncontrolledOpen(next);
    },
    [isControlled, onToggle]
  );
  return (
    <div className={cn('border rounded-lg overflow-hidden', colors.border, colors.surface, className)}>
      <div className={cn('flex w-full items-stretch', headerBarClassName)}>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className={cn(
            'flex min-w-0 flex-1 items-center justify-between gap-3 px-4 py-3 text-left transition-colors',
            colors.text,
            colors.interactiveHover,
            headerClassName
          )}
        >
          <div className="min-w-0 flex-1">
            {typeof title === 'string' ? <span className="font-medium">{title}</span> : title}
            {description && (
              <p className={cn('text-xs mt-0.5 truncate', colors.textMuted)}>{description}</p>
            )}
          </div>
          {!headerAction ? (
            <svg
              className={cn('w-5 h-5 flex-shrink-0 transition-transform', colors.textMuted, open && 'rotate-180')}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          ) : null}
        </button>
        {headerAction ? (
          <div
            className={cn(
              'flex items-center border-l px-1.5',
              colors.border,
              headerActionClassName,
            )}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            role="presentation"
          >
            <button
              type="button"
              onClick={() => setOpen(!open)}
              className={cn(
                'mr-1 flex h-full items-center px-2 transition-colors',
                colors.textMuted,
                colors.interactiveHover,
              )}
              aria-expanded={open}
              aria-label={open ? 'Collapse section' : 'Expand section'}
            >
              <svg
                className={cn('w-5 h-5 flex-shrink-0 transition-transform', open && 'rotate-180')}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {headerAction}
          </div>
        ) : null}
      </div>
      {open && (
        <div className={cn('px-4 pb-4 pt-1 border-t', colors.border, colors.surface)}>
          {children}
        </div>
      )}
    </div>
  );
};

interface AccordionProps {
  children: React.ReactNode;
  className?: string;
  /** Optional: when set, open state is controlled; keys of open sections */
  openKeys?: string[];
  /** Optional: when set, called when a section is toggled; use with openKeys */
  onOpenChange?: (key: string, open: boolean) => void;
}

export const Accordion: React.FC<AccordionProps> = ({ children, className, openKeys: _openKeys, onOpenChange: _onOpenChange }) => {
  return <div className={cn('space-y-2', className)}>{children}</div>;
};
